import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  ArchiveIntakeFormResponse,
  CreateCustomerSubmissionLinkApplicationErrors,
  CreateCustomerSubmissionLinkOptions,
  CreateCustomerSubmissionLinkResponse,
  IntakeFormQuery,
  IntakeFormsQueryBuilder,
  QueryIntakeFormsResponse,
  UnarchiveIntakeFormResponse,
  UpdateIntakeFormExpirationPeriodOptions,
  UpdateIntakeFormExpirationPeriodResponse,
  archiveIntakeForm as universalArchiveIntakeForm,
  createCustomerSubmissionLink as universalCreateCustomerSubmissionLink,
  deleteIntakeForm as universalDeleteIntakeForm,
  queryIntakeForms as universalQueryIntakeForms,
  typedQueryIntakeForms as universalTypedQueryIntakeForms,
  unarchiveIntakeForm as universalUnarchiveIntakeForm,
  updateIntakeFormExpirationPeriod as universalUpdateIntakeFormExpirationPeriod,
} from './intake-forms-v1-intake-form-intake-forms.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/intake-forms' };

export function queryIntakeForms(
  httpClient: HttpClient
): QueryIntakeFormsSignature {
  return () =>
    universalQueryIntakeForms(
      // @ts-ignore
      { httpClient }
    );
}

interface QueryIntakeFormsSignature {
  /**
   * Retrieves a list of intake forms, with the specified paging, filtering, and sorting.
   *
   * Query Intake Forms runs with these defaults, which you can override:
   *
   * - `createdDate` is sorted in `DESC` order.
   * - `paging.limit` is `50`.
   * - `paging.offset` is `0`.
   *
   * Learn more about [API Query Language](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-the-wix-api-query-language).
   */
  (): IntakeFormsQueryBuilder;
}

export function typedQueryIntakeForms(
  httpClient: HttpClient
): TypedQueryIntakeFormsSignature {
  return (query: IntakeFormQuery) =>
    universalTypedQueryIntakeForms(
      query,
      // @ts-ignore
      { httpClient }
    );
}

interface TypedQueryIntakeFormsSignature {
  /** */
  (query: IntakeFormQuery): Promise<
    NonNullablePaths<QueryIntakeFormsResponse, `intakeForms`, 2>
  >;
}

export function updateIntakeFormExpirationPeriod(
  httpClient: HttpClient
): UpdateIntakeFormExpirationPeriodSignature {
  return (
    intakeFormId: string,
    options?: NonNullablePaths<
      UpdateIntakeFormExpirationPeriodOptions,
      `formRevision`,
      2
    >
  ) =>
    universalUpdateIntakeFormExpirationPeriod(
      intakeFormId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateIntakeFormExpirationPeriodSignature {
  /**
   * Updates the expiration period of a specific intake form.
   *
   * When you update the expiration period, the system recalculates expiration for all existing submissions.
   * @param - Intake form ID.
   */
  (
    intakeFormId: string,
    options?: NonNullablePaths<
      UpdateIntakeFormExpirationPeriodOptions,
      `formRevision`,
      2
    >
  ): Promise<UpdateIntakeFormExpirationPeriodResponse>;
}

export function createCustomerSubmissionLink(
  httpClient: HttpClient
): CreateCustomerSubmissionLinkSignature {
  return (
    intakeFormId: string,
    options?: CreateCustomerSubmissionLinkOptions
  ) =>
    universalCreateCustomerSubmissionLink(
      intakeFormId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateCustomerSubmissionLinkSignature {
  /**
   * Creates a shareable link for customers to submit an intake form.
   *
   * The link is valid for 72 hours.
   * @param - Intake form ID.
   */
  (
    intakeFormId: string,
    options?: CreateCustomerSubmissionLinkOptions
  ): Promise<
    CreateCustomerSubmissionLinkResponse & {
      __applicationErrorsType?: CreateCustomerSubmissionLinkApplicationErrors;
    }
  >;
}

export function deleteIntakeForm(
  httpClient: HttpClient
): DeleteIntakeFormSignature {
  return (intakeFormId: string) =>
    universalDeleteIntakeForm(
      intakeFormId,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteIntakeFormSignature {
  /**
   * Deletes an intake form.
   *
   *
   * Also deletes the underlying [Wix form](https://dev.wix.com/docs/api-reference/crm/forms/form-schemas/introduction).
   *
   * Existing [submissions](https://dev.wix.com/docs/api-reference/crm/forms/other-services/intake-forms/intake-form-submissions/introduction) for the intake form aren't automatically deleted but become orphaned.
   * When [querying submissions](https://dev.wix.com/docs/api-reference/crm/forms/other-services/intake-forms/intake-form-submissions/query-intake-form-submissions), orphaned submissions aren't returned.
   * @param - Intake form ID.
   */
  (intakeFormId: string): Promise<void>;
}

export function archiveIntakeForm(
  httpClient: HttpClient
): ArchiveIntakeFormSignature {
  return (intakeFormId: string, formRevision: string) =>
    universalArchiveIntakeForm(
      intakeFormId,
      formRevision,
      // @ts-ignore
      { httpClient }
    );
}

interface ArchiveIntakeFormSignature {
  /**
   * Archives an intake form, disabling it from accepting new submissions.
   *
   * Existing submissions remain accessible. You can [unarchive](https://dev.wix.com/docs/api-reference/crm/forms/other-services/intake-forms/intake-forms/unarchive-intake-form) the form later to resume accepting submissions.
   * @param - Intake form ID.
   * @param - Revision number, which increments by 1 each time the intake form is updated.
   * To prevent conflicting changes, you must specify the current revision when updating the intake form.
   */
  (
    intakeFormId: string,
    formRevision: string
  ): Promise<ArchiveIntakeFormResponse>;
}

export function unarchiveIntakeForm(
  httpClient: HttpClient
): UnarchiveIntakeFormSignature {
  return (intakeFormId: string, formRevision: string) =>
    universalUnarchiveIntakeForm(
      intakeFormId,
      formRevision,
      // @ts-ignore
      { httpClient }
    );
}

interface UnarchiveIntakeFormSignature {
  /**
   * Unarchives an intake form, enabling it to accept new submissions.
   * @param - Intake form ID.
   * @param - Revision number, which increments by 1 each time the intake form is updated.
   * To prevent conflicting changes, you must specify the current revision when updating the intake form.
   */
  (
    intakeFormId: string,
    formRevision: string
  ): Promise<UnarchiveIntakeFormResponse>;
}

export {
  ActionEvent,
  ApprovalMode,
  ArchiveIntakeFormRequest,
  ArchiveIntakeFormResponse,
  ContactDetails,
  CreateCustomerSubmissionLinkOptions,
  CreateCustomerSubmissionLinkRequest,
  CreateCustomerSubmissionLinkResponse,
  CursorPaging,
  CursorPagingMetadata,
  CursorQuery,
  CursorQueryPagingMethodOneOf,
  Cursors,
  DecryptContactDetailsFromTokenRequest,
  DecryptContactDetailsFromTokenResponse,
  DeleteIntakeFormRequest,
  DeleteIntakeFormResponse,
  DomainEvent,
  DomainEventBodyOneOf,
  Empty,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  IntakeForm,
  IntakeFormQuerySpec,
  IntakeFormsQueryBuilder,
  IntakeFormsQueryResult,
  PendingTriggerField,
  QueryIntakeFormsRequest,
  QueryIntakeFormsResponse,
  RestoreInfo,
  SortOrder,
  Sorting,
  UnarchiveIntakeFormRequest,
  UnarchiveIntakeFormResponse,
  UpdateIntakeFormExpirationPeriodOptions,
  UpdateIntakeFormExpirationPeriodRequest,
  UpdateIntakeFormExpirationPeriodResponse,
  UpdateIntakeFormSettingsRequest,
  UpdateIntakeFormSettingsResponse,
  utils,
} from './intake-forms-v1-intake-form-intake-forms.universal.js';
